<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import oidcAuth from '@/auth/oidcAuth'
import { useAuthStore } from '@/stores/useAuthStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { initCoreSessionHandler } from '@/auth/coreSessionHandler'
import { initAuthListeners } from '@/auth/oidcAuth'
import { useUserStore } from '@/stores/useUserStore'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import { useCampaignStore } from '@/stores/useCampaignStore'
import { useKpiStore } from '@/stores/useKpiStore'
import { useTitleStore } from '@/stores/useTitleStore'
import apyMark from '@/v2/assets/brand/apy-mark.svg'

const router = useRouter()

const mode = ref<'choice' | 'password'>('choice')
const email = ref('')
const password = ref('')
const error = ref('')
const loading = ref(false)

// Guard against concurrent invocations: onMounted starts a check, then a
// `core_auth_login` storage event starts another. If the first one's awaited
// fetchMe resolves *after* the second's, both reach the redirect block — the
// late one reads sessionStorage AFTER the early one consumed `oidc_redirect`,
// falls back to '/', and stomps the correct push. So: redirect at most once.
let alreadyRedirected = false

// If another tab signed back in (cookies + localStorage now hold a valid
// session), auto-redirect this stale /login tab to its saved per-tab URL.
async function checkAuthAndMaybeRedirect() {
  if (alreadyRedirected) return

  const boostCoreStore = useBoostCorePermissionStore()
  const isOidcAuth = oidcAuth.isAuthenticated
  const isPasswordAuth = boostCoreStore.authMethod === 'password' && boostCoreStore.user !== null
  if (!isOidcAuth && !isPasswordAuth) {
    // Last-resort: probe the cookie via /me (cheap, idempotent).
    const me = await boostCoreStore.fetchMe().catch(() => null)
    if (!me) return
  }

  // Re-check after the await — another concurrent call may have already
  // consumed oidc_redirect and pushed.
  if (alreadyRedirected) return
  alreadyRedirected = true

  const redirectPath = sessionStorage.getItem('oidc_redirect') || '/'
  sessionStorage.removeItem('oidc_redirect')
  router.push(redirectPath)
}

// React only to the dedicated "login established" sentinel — written by
// coreLogin / coreLoginPassword on success. Heartbeat updates (coreRefreshState)
// and rotation-only csrf updates don't fire this, so we don't spam /me.
function onStorageEvent(ev: StorageEvent) {
  if (ev.key !== 'core_auth_login' || !ev.newValue) return
  checkAuthAndMaybeRedirect()
}

onMounted(() => {
  checkAuthAndMaybeRedirect()
  window.addEventListener('storage', onStorageEvent)
})

onUnmounted(() => {
  window.removeEventListener('storage', onStorageEvent)
})

function loginMicrosoft() {
  oidcAuth.signIn()
}

async function loginPassword() {
  error.value = ''
  if (!email.value || !password.value) {
    error.value = 'Veuillez remplir tous les champs.'
    return
  }
  loading.value = true
  try {
    const authStore = useAuthStore()
    const result = await authStore.coreLoginPassword(email.value, password.value)
    if (!result.ok) {
      if (result.error === 'account_locked') {
        error.value = 'Compte temporairement verrouillé. Réessayez plus tard.'
      } else if (result.error === 'password_rotation_required') {
        error.value = 'Vous devez réinitialiser votre mot de passe.'
      } else if (result.error === 'user_disabled') {
        error.value = 'Ce compte est désactivé.'
      } else {
        error.value = 'Email ou mot de passe incorrect.'
      }
      return
    }

    // Initialize the app after successful password login
    initCoreSessionHandler()
    initAuthListeners()

    const boostCoreStore = useBoostCorePermissionStore()
    await boostCoreStore.fetchMe()

    // Init legacy user store via email (getMe() requires OIDC, getUserByEmail works for both)
    const userStore = useUserStore()
    if (boostCoreStore.user?.email) {
      await userStore.getUserByEmail(boostCoreStore.user.email)
    }
    if (userStore.userConnected) {
      userStore.getAllUsers()
      useSelectionsStore().getAll()
      useCampaignStore().getManagerCampaigns()
      useCampaignStore().getMyPrivateCampaign()
      useKpiStore().getMyKpisOpportunities()
      useTitleStore().getTitles()
    }

    const redirectPath = sessionStorage.getItem('oidc_redirect') || '/'
    sessionStorage.removeItem('oidc_redirect')
    router.push(redirectPath)
  } catch (e) {
    error.value = 'Une erreur est survenue. Veuillez réessayer.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen flex items-center justify-center bg-base-200">
    <div class="card w-full max-w-md bg-base-100 shadow-xl">
      <div class="card-body items-center text-center">
        <div class="mb-4">
          <component :is="apyMark" class="h-20 w-20 mx-auto" aria-hidden="true" />
        </div>
        <h2 class="card-title text-xl mb-6">Connexion à APY</h2>

        <!-- Mode choix -->
        <template v-if="mode === 'choice'">
          <button class="btn btn-primary w-full gap-2" @click="loginMicrosoft">
            <svg class="w-5 h-5" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Se connecter avec Microsoft
          </button>

          <div class="divider text-xs text-base-content/50">OU</div>

          <button class="btn btn-outline w-full" @click="mode = 'password'">
            Se connecter avec email
          </button>
        </template>

        <!-- Mode email/password -->
        <template v-if="mode === 'password'">
          <form class="w-full space-y-4" @submit.prevent="loginPassword">
            <div class="form-control">
              <label class="label"><span class="label-text">Email</span></label>
              <input
                v-model="email"
                type="email"
                placeholder="votre@email.com"
                class="input input-bordered w-full"
                autocomplete="username"
                required
              />
            </div>

            <div class="form-control">
              <label class="label"><span class="label-text">Mot de passe</span></label>
              <input
                v-model="password"
                type="password"
                placeholder="••••••••"
                class="input input-bordered w-full"
                autocomplete="current-password"
                required
              />
            </div>

            <div v-if="error" class="alert alert-error text-sm py-2">
              {{ error }}
            </div>

            <button
              type="submit"
              class="btn btn-primary w-full"
              :disabled="loading"
            >
              <span v-if="loading" class="loading loading-spinner loading-sm"></span>
              {{ loading ? 'Connexion...' : 'Se connecter' }}
            </button>
          </form>

          <div class="flex justify-between w-full mt-2 text-sm">
            <button class="link link-hover text-base-content/60" @click="mode = 'choice'">
              Retour
            </button>
            <router-link to="/auth/forgot-password" class="link link-hover text-base-content/60">
              Mot de passe oublié ?
            </router-link>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
